import { z } from "zod";
import type { GrowthTaskKind } from "./growth";
import { GrowthTaskKindSchema } from "./growth";

const DateTimeSchema = z.string().datetime({ offset: true });
const FieldKeySchema = z.string().regex(/^[a-z][a-z0-9_]*$/);

// 字段值的锚点是 growth_tasks.id（uuid，见 005_quality_growth_operations.sql:100）。
// step_id 是节点内部的分组标识（如 "product_feasibility:1"），主干没有步骤表，因此是 text。
const TaskIdSchema = z.string().uuid();
const StepIdSchema = z.string().min(1).max(120);

export const TaskFieldControlSchema = z.enum([
  "text", "textarea", "number", "money", "select", "multi_select", "tags", "boolean",
]);
export type TaskFieldControl = z.infer<typeof TaskFieldControlSchema>;

export const TaskFieldAnswerStatusSchema = z.enum(["known", "unknown", "not_applicable"]);
export type TaskFieldAnswerStatus = z.infer<typeof TaskFieldAnswerStatusSchema>;

export const TaskFieldSourceSchema = z.enum(["profile", "form", "chat", "page", "file", "system"]);
export type TaskFieldSource = z.infer<typeof TaskFieldSourceSchema>;

export const TaskFieldOptionSchema = z.object({
  value: z.string().min(1).max(120),
  label: z.string().min(1).max(120),
}).strict();
export type TaskFieldOption = z.infer<typeof TaskFieldOptionSchema>;

export const TaskFieldDefinitionSchema = z.object({
  key: FieldKeySchema,
  label: z.string().min(1).max(80),
  description: z.string().min(1).max(300),
  control: TaskFieldControlSchema,
  required: z.boolean(),
  allowUnknown: z.boolean().default(false),
  evidenceRequired: z.boolean().default(false),
  options: z.array(TaskFieldOptionSchema).min(1).max(30).optional(),
  unit: z.string().min(1).max(24).optional(),
}).strict().superRefine((field, context) => {
  if ((field.control === "select" || field.control === "multi_select") && !field.options?.length) {
    context.addIssue({ code: "custom", path: ["options"], message: "选择字段必须提供选项" });
  }
});
export type TaskFieldDefinition = z.infer<typeof TaskFieldDefinitionSchema>;

export const TaskFieldValueSchema = z.object({
  id: z.string().uuid(),
  taskId: TaskIdSchema,
  stepId: StepIdSchema,
  fieldKey: FieldKeySchema,
  value: z.unknown(),
  answerStatus: TaskFieldAnswerStatusSchema,
  source: TaskFieldSourceSchema,
  confirmed: z.boolean(),
  updatedAt: DateTimeSchema,
}).strict();
export type TaskFieldValue = z.infer<typeof TaskFieldValueSchema>;

export const TaskFieldSaveValueSchema = z.object({
  fieldKey: FieldKeySchema,
  value: z.unknown(),
  answerStatus: TaskFieldAnswerStatusSchema,
}).strict().superRefine((item, context) => {
  if (item.answerStatus === "known" && (item.value === undefined || item.value === null || item.value === "")) {
    context.addIssue({ code: "custom", path: ["value"], message: "已知字段必须提供值" });
  }
});
export type TaskFieldSaveValue = z.infer<typeof TaskFieldSaveValueSchema>;

// source 只允许 form / chat：profile 由服务端从经营资料同步，客户端不得自行声明来源。
// confirmed 与 userId 不接受客户端传入——写入即确认，归属取自会话。
export const TaskFieldSaveRequestSchema = z.object({
  source: z.enum(["form", "chat"]),
  values: z.array(TaskFieldSaveValueSchema).min(1).max(30),
}).strict();
export type TaskFieldSaveRequest = z.infer<typeof TaskFieldSaveRequestSchema>;

export const TaskFieldsStateSchema = z.object({
  taskId: TaskIdSchema,
  stepId: StepIdSchema,
  stepPosition: z.number().int().positive(),
  definitions: z.array(TaskFieldDefinitionSchema),
  values: z.array(TaskFieldValueSchema),
  missingRequiredFieldKeys: z.array(FieldKeySchema),
  canComplete: z.boolean(),
}).strict();
export type TaskFieldsState = z.infer<typeof TaskFieldsStateSchema>;

export const TaskFieldProposalValueSchema = z.object({
  fieldKey: FieldKeySchema,
  value: z.unknown(),
  answerStatus: TaskFieldAnswerStatusSchema,
  sourceSummary: z.string().min(1).max(240),
  confidence: z.enum(["high", "medium", "low"]).default("medium"),
}).strict().superRefine((item, context) => {
  if (item.answerStatus === "known" && (item.value === undefined || item.value === null || item.value === "")) {
    context.addIssue({ code: "custom", path: ["value"], message: "已知字段必须提供值" });
  }
});
export type TaskFieldProposalValue = z.infer<typeof TaskFieldProposalValueSchema>;

export const TaskFieldProposalSchema = z.object({
  taskId: TaskIdSchema,
  stepId: StepIdSchema,
  values: z.array(TaskFieldProposalValueSchema).min(1).max(20),
}).strict();
export type TaskFieldProposal = z.infer<typeof TaskFieldProposalSchema>;

export const GrowthTaskContextSchema = z.object({
  taskId: TaskIdSchema,
  stepId: StepIdSchema,
  taskTitle: z.string().min(1).max(160),
  stepTitle: z.string().min(1).max(120),
  definitions: z.array(TaskFieldDefinitionSchema).max(30),
  values: z.array(TaskFieldValueSchema).max(30),
  missingRequiredFieldKeys: z.array(FieldKeySchema).max(30),
}).strict();
export type GrowthTaskContext = z.infer<typeof GrowthTaskContextSchema>;

const yesNoOptions: TaskFieldOption[] = [
  { value: "yes", label: "是" },
  { value: "no", label: "否" },
];
const businessTypeOptions: TaskFieldOption[] = [
  { value: "individual", label: "个人" },
  { value: "sole_proprietor", label: "个体工商户" },
  { value: "company", label: "公司" },
];
const experienceOptions: TaskFieldOption[] = [
  { value: "preparing", label: "正在准备" },
  { value: "beginner", label: "刚开始运营" },
  { value: "operating", label: "已经在运营" },
  { value: "scaling", label: "正在扩大规模" },
];
const platformOptions: TaskFieldOption[] = [
  { value: "tiktok_shop", label: "TikTok Shop" },
  { value: "temu", label: "Temu" },
  { value: "shopee", label: "Shopee" },
];
const shippingMethodOptions: TaskFieldOption[] = [
  { value: "domestic_direct", label: "国内直发" },
  { value: "dropshipping", label: "一件代发" },
  { value: "platform_logistics", label: "平台物流" },
  { value: "overseas_warehouse", label: "海外仓" },
];
const returnReasonOptions: TaskFieldOption[] = [
  { value: "quality", label: "商品质量" },
  { value: "not_as_described", label: "描述不符" },
  { value: "damaged_in_transit", label: "物流破损" },
  { value: "buyer_choice", label: "买家原因" },
  { value: "other", label: "其他" },
];
const scaleDecisionOptions: TaskFieldOption[] = [
  { value: "scale", label: "达到目标，加预算" },
  { value: "hold", label: "继续观察" },
  { value: "fix_product", label: "先改商品或定向" },
  { value: "stop", label: "停止投放" },
];

function field(
  key: string,
  label: string,
  description: string,
  control: TaskFieldControl,
  options: Partial<Pick<TaskFieldDefinition, "required" | "allowUnknown" | "evidenceRequired" | "options" | "unit">> = {},
): TaskFieldDefinition {
  return TaskFieldDefinitionSchema.parse({
    key,
    label,
    description,
    control,
    required: options.required ?? true,
    allowUnknown: options.allowUnknown ?? false,
    evidenceRequired: options.evidenceRequired ?? false,
    options: options.options,
    unit: options.unit,
  });
}

const rawCatalog: Record<string, TaskFieldDefinition[]> = {
  "business_assessment:1": [
    field("target_markets", "目标市场", "计划进入的主要市场区域。", "tags"),
    field("target_countries", "目标国家", "优先经营的国家或地区。", "tags", { allowUnknown: true }),
    field("categories", "经营类目", "准备经营的商品类目。", "tags"),
    field("stage_goal", "阶段目标", "当前阶段最想达成的可验证结果。", "textarea"),
  ],
  "business_assessment:2": [
    field("business_type", "主体类型", "当前用于经营的主体类型。", "select", { options: businessTypeOptions }),
    field("experience_stage", "当前经验", "目前的跨境电商经营阶段。", "select", { options: experienceOptions }),
    field("budget_cents", "可用预算", "可投入本阶段的总预算。", "money", { allowUnknown: true, unit: "元" }),
    field("weekly_hours", "每周可用时间", "每周可以稳定投入的时间。", "number", { unit: "小时" }),
  ],
  "business_assessment:3": [
    field("strengths", "当前优势", "当前已有的资源、能力或供应链优势。", "textarea"),
    field("constraints", "主要限制", "会影响执行的时间、资金或能力限制。", "textarea", { allowUnknown: true }),
    field("primary_risk", "首要风险", "目前最可能阻碍目标实现的风险。", "textarea"),
  ],
  "platform_selection:1": [
    field("qualification_fit", "资质适配", "现有主体与资质对各平台的适配情况。", "textarea"),
    field("market_coverage", "市场覆盖", "平台覆盖目标国家和用户的情况。", "textarea"),
    field("category_fit", "类目适配", "目标类目在各平台的适配情况。", "textarea"),
  ],
  "platform_selection:2": [
    field("entry_budget", "预算门槛", "各平台启动与周转预算的差异。", "textarea"),
    field("operation_requirements", "运营要求", "各平台对时间、内容和履约的要求。", "textarea"),
    field("constraint_match", "约束匹配", "现有资源与平台要求的匹配结论。", "textarea"),
  ],
  "platform_selection:3": [
    field("selected_platform", "最终平台", "本阶段明确选择的目标平台。", "select", { options: platformOptions }),
    field("platform_reason", "选择理由", "结合证据说明为什么选择该平台。", "textarea"),
  ],
  "store_registration:1": [
    field("qualification_status", "资质状态", "平台要求的主体和类目资质是否具备。", "textarea"),
    field("official_requirements_checked", "已核对官方要求", "是否依据当前官方要求完成核对。", "boolean", { options: yesNoOptions, evidenceRequired: true }),
  ],
  "store_registration:2": [
    field("account_status", "账号状态", "账号申请或准备进度，不填写任何登录凭据。", "text"),
    field("non_sensitive_documents", "非敏感资料清单", "已准备的营业执照、类目证明等非敏感资料。", "tags"),
    field("missing_documents", "缺失资料", "尚未准备好的非敏感资料。", "tags", { allowUnknown: true }),
  ],
  "store_registration:3": [
    field("registration_checklist", "注册步骤清单", "已核对的注册步骤与当前状态。", "textarea"),
    field("registration_blocker", "当前阻塞项", "阻碍继续注册的事项，没有则填写无。", "textarea", { allowUnknown: true }),
  ],
  "product_feasibility:1": [
    field("source_product_url", "货源链接", "候选商品的货源页面地址。", "text", { evidenceRequired: true }),
    field("purchase_price_cents", "采购价", "单件采购价格。", "money", { allowUnknown: true, unit: "元" }),
    field("target_price_cents", "目标售价", "计划在目标市场销售的价格。", "money", { allowUnknown: true, unit: "元" }),
  ],
  "product_feasibility:2": [
    field("demand_signals", "需求信号", "销量、搜索、内容热度等需求依据。", "textarea", { evidenceRequired: true }),
    field("competitor_count", "竞品数量", "可比竞品的数量或竞争密度。", "number", { allowUnknown: true }),
    field("review_problems", "评价问题", "竞品评价中反复出现的痛点。", "textarea", { allowUnknown: true }),
  ],
  "product_feasibility:3": [
    field("logistics_profile", "物流属性", "重量、体积、易碎、带电等物流特征。", "textarea"),
    field("certification_risk", "认证风险", "目标市场可能要求的认证。", "textarea", { allowUnknown: true }),
    field("infringement_risk", "侵权风险", "商标、专利、图片或外观风险。", "textarea", { allowUnknown: true }),
  ],
  "supplier_profit:1": [
    field("minimum_order_quantity", "起订量", "供应商给出的最小采购数量。", "number", { allowUnknown: true }),
    field("tier_prices", "阶梯价格", "不同采购数量对应的报价。", "textarea", { evidenceRequired: true }),
    field("lead_time_days", "交期", "从下单到可发货的天数。", "number", { allowUnknown: true, unit: "天" }),
  ],
  "supplier_profit:2": [
    field("logistics_cost_cents", "物流费", "单件头程与尾程物流成本。", "money", { allowUnknown: true, unit: "元" }),
    field("platform_fee_cents", "平台费", "单件或按比例折算的平台费用。", "money", { allowUnknown: true, unit: "元" }),
    field("marketing_cost_cents", "营销费", "单件预估营销成本。", "money", { allowUnknown: true, unit: "元" }),
    field("return_rate_percent", "退货率", "预估退货率。", "number", { allowUnknown: true, unit: "%" }),
  ],
  "supplier_profit:3": [
    field("target_margin_percent", "目标利润率", "希望达到的利润率。", "number", { unit: "%" }),
    field("break_even_units", "保本销量", "覆盖固定和首批成本需要的销量。", "number", { allowUnknown: true }),
    field("first_batch_cash_cents", "首批现金需求", "首批采购、物流和营销的现金需求。", "money", { allowUnknown: true, unit: "元" }),
  ],
  "listing_preparation:1": [
    field("listing_title", "商品标题", "待上架商品的标题草稿。", "text"),
    field("selling_points", "核心卖点", "面向目标市场的主要卖点。", "tags"),
    field("specifications", "规格属性", "颜色、尺寸、材质和包装等信息。", "textarea"),
    field("image_status", "图片准备状态", "主图和详情图的准备情况。", "textarea"),
  ],
  "listing_preparation:2": [
    field("final_price_cents", "最终定价", "结合利润测算后的上架价格。", "money", { unit: "元" }),
    field("compliance_status", "合规状态", "平台和目标国合规项的核对结果。", "textarea", { evidenceRequired: true }),
  ],
  "listing_preparation:3": [
    field("draft_review", "草稿复核", "标题、属性、图片、价格和合规的最终复核。", "textarea"),
    field("remaining_questions", "待确认项", "上架前仍需确认的事项，没有则填写无。", "textarea", { allowUnknown: true }),
  ],
  "fulfillment_service:1": [
    field("shipping_method", "物流方式", "本店实际使用的发货方式。", "select", { options: shippingMethodOptions, evidenceRequired: true }),
    field("shipping_template_name", "运费模板", "店铺后台使用的运费模板名称。", "text", { allowUnknown: true }),
    field("handling_time_days", "备货时长", "从订单产生到交给物流商的天数。", "number", { allowUnknown: true, unit: "天" }),
  ],
  "fulfillment_service:2": [
    field("carrier", "承运商", "本次实际使用的承运商名称。", "text"),
    field("tracking_number", "物流单号", "按实际面单填写，不填写未发出的单号。", "text", { evidenceRequired: true }),
    field("dispatch_on_time", "是否按时发货", "是否在平台要求时效内交给物流商。", "boolean", { options: yesNoOptions }),
  ],
  "fulfillment_service:3": [
    field("buyer_question_topics", "买家问题类型", "买家最常询问的问题类型。", "tags", { allowUnknown: true }),
    field("return_reason_category", "退货原因", "本次售后对应的主要原因。", "select", { options: returnReasonOptions, allowUnknown: true }),
    field("after_sales_outcome", "售后处理结果", "本次售后的处理方式与结果。", "textarea", { allowUnknown: true }),
  ],
  "growth_operations:1": [
    field("listing_ready", "商品页已就绪", "主图、标题、详情与价格是否已核对。", "boolean", { options: yesNoOptions, evidenceRequired: true }),
    field("inventory_ready", "库存可支撑", "库存能否支撑计划的投放量。", "boolean", { options: yesNoOptions }),
    field("fulfillment_healthy", "履约指标达标", "发货时效与履约指标是否达标。", "boolean", { options: yesNoOptions, evidenceRequired: true }),
  ],
  "growth_operations:2": [
    field("break_even_ad_spend_cents", "可承担广告费", "单笔订单可承担的广告费用。", "money", { unit: "元" }),
    field("break_even_roas", "保本 ROAS", "销售额除以可承担广告费得到的保本值。", "number"),
    field("observation_period_days", "观察周期", "本轮计划观察的天数。", "number", { unit: "天" }),
  ],
  "growth_operations:3": [
    field("observed_ctr_percent", "点击率", "本轮实测点击率。", "number", { allowUnknown: true, unit: "%" }),
    field("observed_orders", "成交单数", "本轮实际成交订单数。", "number", { allowUnknown: true }),
    field("scale_decision", "下一步决定", "依据实测数据选择下一步动作。", "select", { options: scaleDecisionOptions }),
  ],
  "independent_review:1": [
    field("new_product", "新商品", "用于独立复核的相似新商品。", "text", { evidenceRequired: true }),
    field("selection_reason", "选择理由", "为什么它适合作为独立复核案例。", "textarea"),
  ],
  "independent_review:2": [
    field("independent_judgement", "自主判断", "不依赖 AI 结论完成的可行性判断。", "textarea"),
    field("evidence_sources", "证据来源", "支撑判断的页面或资料来源。", "textarea", { evidenceRequired: true }),
    field("operation_result", "操作结果", "实际完成的操作与结果。", "textarea"),
  ],
  "independent_review:3": [
    field("retrospective", "复盘", "本次判断正确与不足之处。", "textarea"),
    field("prompt_dependency", "提示依赖", "本次需要 AI 提示的程度和节点。", "textarea"),
    field("next_action", "下一步行动", "下一轮独立运营的具体行动。", "textarea"),
  ],
};

/**
 * 统一商品阶段（`product_launch`）的字段目录**由旧三个阶段派生**，不重新抄一遍。
 *
 * 合并只换路线外壳，不换完成口径：迁移会把旧三个任务的字段行重新归属到统一任务，
 * 并映射到下面这三个步骤位置。因此这三个目录必须与旧目录**逐字段一致**——
 * 抄一份出来就会漂移，而漂移的表现是「迁移后用户被要求补一个他从没见过的必填项」。
 *
 * 步骤映射（旧 → 新）：
 * - product_feasibility:1..3 → product_launch:1（商品初筛事实）
 * - supplier_profit:1..3     → product_launch:2（供应商与利润事实）
 * - listing_preparation:1..3 → product_launch:3（Listing 准备事实）
 */
const PRODUCT_LAUNCH_CATALOG_SOURCES: Readonly<Record<string, readonly string[]>> = {
  "product_launch:1": ["product_feasibility:1", "product_feasibility:2", "product_feasibility:3"],
  "product_launch:2": ["supplier_profit:1", "supplier_profit:2", "supplier_profit:3"],
  "product_launch:3": ["listing_preparation:1", "listing_preparation:2", "listing_preparation:3"],
};
for (const [target, sources] of Object.entries(PRODUCT_LAUNCH_CATALOG_SOURCES)) {
  rawCatalog[target] = sources.flatMap((source) => rawCatalog[source] ?? []);
}

// 目录必须覆盖每个 task kind 的每个步骤位置；缺项在模块加载时即失败，不留到运行时。
for (const taskKind of GrowthTaskKindSchema.options) {
  for (let stepPosition = 1; stepPosition <= 3; stepPosition += 1) {
    const key = taskKind + ":" + stepPosition;
    const definitions = rawCatalog[key];
    if (!definitions?.length) throw new Error("Missing task field catalog: " + key);
    if (new Set(definitions.map((definition) => definition.key)).size !== definitions.length) {
      throw new Error("Duplicate task field key: " + key);
    }
  }
}

export const STANDARD_TASK_FIELD_CATALOG: Readonly<Record<string, readonly TaskFieldDefinition[]>> = rawCatalog;

export function getTaskFieldDefinitions(
  taskKind: GrowthTaskKind,
  stepPosition: number,
): TaskFieldDefinition[] {
  const definitions = STANDARD_TASK_FIELD_CATALOG[taskKind + ":" + stepPosition] ?? [];
  return definitions.map((definition) => ({
    ...definition,
    options: definition.options?.map((option) => ({ ...option })),
  }));
}
